// Frequency range constants
const MIN_FREQ = 0.0007; // 700 kHz
const MAX_FREQ = 1e9; // 1 GHz

// Call sign generation constants
const PREFIX_LETTERS = "KNWA";
const NUMBERS = "0123456789";
const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

// Quantum constants
const PLANCK_CONSTANT = 6.62607015e-34;
const BOLTZMANN_CONSTANT = 1.380649e-23;
const TEMPERATURE = 300; // Room temperature in Kelvin

/**
 * Generates a random frequency within specified band constraints
 * @param {object} p5 - p5.js instance for random number generation
 * @param {number} minFreq - Minimum frequency
 * @param {number} maxFreq - Maximum frequency
 * @returns {number} - Generated frequency
 */
export const generateRandomFrequency = (p5, minFreq = MIN_FREQ, maxFreq = MAX_FREQ) => {
  const bandSelector = p5.random(8);
  const bandMin = minFreq * Math.pow(1000, bandSelector);
  const bandMax = bandMin * 1000;
  return Math.exp(p5.random(Math.log(bandMin), Math.log(bandMax)));
};

/**
 * Generates quantum metrics using advanced quantum physics models
 * @param {object} p5 - p5.js instance
 * @param {number} frameCount - Current frame count
 * @returns {object} - Object containing quantum metrics
 */
export const generateQuantumMetrics = (p5, frameCount) => {
  // Calculate base values using Perlin noise for smooth transitions
  const baseCoherence = p5.noise(frameCount * 0.01);
  const baseEntanglement = p5.noise(frameCount * 0.02 + 1000);
  const basePhase = p5.noise(frameCount * 0.015 + 2000);

  // Apply quantum decoherence effects
  const decoherenceTime = 1000; // milliseconds
  const decoherenceFactor = Math.exp(-frameCount / decoherenceTime);
  
  // Calculate thermal noise effects
  const thermalEnergy = BOLTZMANN_CONSTANT * TEMPERATURE;
  const thermalNoise = Math.sqrt(thermalEnergy) * p5.random(-1, 1);

  // Generate entanglement coefficient with quantum tunneling effects
  const tunnelProbability = Math.exp(-frameCount * 0.001);
  const entanglement = (baseEntanglement * 0.8 + 0.2) * (1 - tunnelProbability);

  // Generate quantum coherence with environmental interaction
  const environmentalDephasing = Math.sin(frameCount * 0.005) * 0.1;
  const coherence = Math.max(0, Math.min(1,
    (baseCoherence * 0.7 + 0.3) * decoherenceFactor - environmentalDephasing
  ));

  // Generate quantum phase with quantum interference patterns
  const interferencePattern = Math.sin(frameCount * 0.02) * Math.cos(frameCount * 0.015);
  const phase = (basePhase * p5.TWO_PI + interferencePattern) % p5.TWO_PI;

  return {
    entanglement_coefficient: entanglement,
    quantum_coherence: coherence,
    quantum_phase: phase,
    quantum_energy: PLANCK_CONSTANT * frameCount + thermalNoise,
    decoherence_rate: 1 - decoherenceFactor,
    interference_strength: Math.abs(interferencePattern)
  };
};

/**
 * Generates signal metrics with quantum noise considerations
 * @param {object} p5 - p5.js instance
 * @returns {object} - Object containing signal metrics
 */
export const generateSignalMetrics = (p5) => {
  // Generate base metrics
  const baseSnr = p5.random(5, 8);
  const baseQFactor = 50 + p5.random(-10, 10);
  const baseBandwidth = 2.4 + p5.random(-0.2, 0.2);

  // Add quantum noise effects
  const quantumNoise = Math.sqrt(PLANCK_CONSTANT * baseSnr) * p5.random(-1, 1);
  const quantumFluctuations = Math.sin(Date.now() * 0.001) * 0.1;

  return {
    snr: baseSnr + quantumNoise,
    qFactor: baseQFactor * (1 + quantumFluctuations),
    bandwidth: baseBandwidth,
    quantumNoise: Math.abs(quantumNoise),
    fluctuationAmplitude: Math.abs(quantumFluctuations)
  };
};

/**
 * Determines if a state transition should occur based on quantum probability
 * @param {object} p5 - p5.js instance
 * @param {number} probability - Base probability of transition (0-1)
 * @returns {boolean} - Whether transition should occur
 */
export const shouldTransitionState = (p5, probability = 0.005) => {
  // Apply quantum tunneling effect to transition probability
  const tunnelFactor = Math.exp(-10 * probability);
  const quantumProbability = probability * (1 + tunnelFactor);
  return p5.random(1) < quantumProbability;
};

/**
 * Generates a random call sign
 * @returns {string} - Generated call sign
 */
export const generateCallSign = () => {
  let result = '';
  
  // Add prefix letter
  result += PREFIX_LETTERS[Math.floor(Math.random() * PREFIX_LETTERS.length)];
  
  // Add number
  result += NUMBERS[Math.floor(Math.random() * NUMBERS.length)];
  
  // Add suffix letters
  const suffixLength = Math.floor(Math.random() * 3) + 1;
  for (let i = 0; i < suffixLength; i++) {
    result += LETTERS[Math.floor(Math.random() * LETTERS.length)];
  }
  
  return result;
};

/**
 * Applies frequency jitter with quantum effects
 * @param {number} frequency - Base frequency
 * @param {number} frameCount - Current frame count
 * @returns {number} - Frequency with applied quantum jitter
 */
export const applyFrequencyJitter = (frequency, frameCount) => {
  // Combine classical and quantum jitter effects
  const classicalJitter = Math.sin(frameCount * 0.1) * 0.001;
  const quantumJitter = (Math.random() - 0.5) * 0.0005;
  const totalJitter = classicalJitter + quantumJitter;
  
  return frequency * (1.0 + totalJitter);
};

// Export constants for use in other files
export const FREQUENCY_CONSTANTS = {
  MIN_FREQ,
  MAX_FREQ
};